import React, { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { createPortal } from "react-dom";
import BseLoginCard from "../common/BseLoginCard";

const Loader = () => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(255,255,255,0.9)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 999999, // VERY IMPORTANT
    }}
  >
    <div style={{ textAlign: "center" }}>
      <div
        className="spinner-border text-primary"
        style={{ width: "4rem", height: "4rem", borderWidth: "5px" }}
        role="status"
      />
      <div style={{ marginTop: 12, fontWeight: 600, color: "#0d233e" }}>
        Processing...
      </div>
    </div>
  </div>
);

export default function AdminNavbar() {
  // load app CSS (optional if already loaded elsewhere)
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "/assets/css/style.css";
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  // useEffect(() => {
  //   const link = document.createElement("link");
  //   link.rel = "stylesheet";
  //   link.href = "/css/style.css";
  //   document.head.appendChild(link);
  //   return () => {
  //     document.head.removeChild(link);
  //   };
  // }, []);

  // collapsed state
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("sidebar_collapsed") === "true",
  );

  // detect mobile/tablet
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 991 : false,
  );

  // for mobile overlay open/close
  const [mobileOpen, setMobileOpen] = useState(false);

  // inline dropdown (when sidebar expanded) - value is the name of open inline menu or null
  const [inlineOpen, setInlineOpen] = useState(null); // "users" | "mutual-funds" | "research" | "security" | "cms" | "portfolio-management" | "invest-online-bse" | "utilities" | "brokerage" | "risk-profiling" | "billing" | null

  // floating open (when sidebar collapsed) - value is the name of floating menu or null
  const [floatingOpen, setFloatingOpen] = useState(null);

  // loading (for logout)
  const [loading, setLoading] = useState(false);

  // refs for parents (used to compute floating position)
  const usersRef = useRef(null);
  const mutualFundsRef = useRef(null);
  const researchRef = useRef(null);
  const securityRef = useRef(null);
  const cmsRef = useRef(null);
  const portfolioManagementRef = useRef(null);
  const investOnlineBseRef = useRef(null);
  const utilitiesRef = useRef(null);
  const brokerageRef = useRef(null);
  const riskProfilingRef = useRef(null);
  const billingRef = useRef(null);
  const marketingResourcesRef = useRef(null); // kept for compatibility; Marketing Resources is now a direct link, ref unused
  const floatingRef = useRef(null);

  // computed top for floating menu
  const [floatingTop, setFloatingTop] = useState(120);

  // update CSS variable for main-content reflow
  useEffect(() => {
    const width = collapsed ? "64px" : "220px";
    document.documentElement.style.setProperty("--sidebar-width", width);
    if (collapsed) document.body.classList.add("sidebar-collapsed");
    else document.body.classList.remove("sidebar-collapsed");
  }, [collapsed]);

  // auto-detect window width changes (for responsive)
  useEffect(() => {
    function onResize() {
      const small = window.innerWidth <= 991;
      setIsMobile(small);

      // if switching to mobile view, close inline and optionally collapse sidebar
      if (small) {
        // keep previous collapsed state but we want sidebar as overlay on mobile
        // For smoother UX, keep collapsed true on very small devices
        // Do not forcibly change `collapsed` if the user has explicit preference
        setMobileOpen(false); // close overlay on resize by default
      } else {
        // when moving to desktop, ensure mobile overlay closed
        setMobileOpen(false);
      }
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // when collapsed, close inline; when expanded, close floating
  useEffect(() => {
    if (collapsed) {
      setInlineOpen(null);
    } else {
      setFloatingOpen(null);
    }
  }, [collapsed]);

  // compute floating top when floatingOpen changes
  useEffect(() => {
    const ref =
      floatingOpen === "users"
        ? usersRef
        : floatingOpen === "mutual-funds"
          ? mutualFundsRef
          : floatingOpen === "research"
            ? researchRef
            : floatingOpen === "security"
              ? securityRef
              : floatingOpen === "cms"
                ? cmsRef
                : floatingOpen === "portfolio-management"
                  ? portfolioManagementRef
                  : floatingOpen === "invest-online-bse"
                    ? investOnlineBseRef
                    : floatingOpen === "utilities"
                      ? utilitiesRef
                      : floatingOpen === "brokerage"
                        ? brokerageRef
                        : floatingOpen === "risk-profiling"
                          ? riskProfilingRef
                          : floatingOpen === "billing"
                            ? billingRef
                            : null;

    if (ref && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      // Bound floatingTop so it doesn't overflow the viewport
      const top = Math.max(
        64,
        Math.min(window.innerHeight - 160, rect.top + rect.height / 2),
      );
      setFloatingTop(top);
    }
  }, [floatingOpen, collapsed]);

  // click outside or Esc closes floating menu & mobile overlay
  useEffect(() => {
    function onDocClick(e) {
      if (
        floatingOpen &&
        floatingRef.current &&
        !floatingRef.current.contains(e.target)
      ) {
        // ensure click wasn't inside the triggering icon
        const trigger =
          floatingOpen === "users"
            ? usersRef.current
            : floatingOpen === "mutual-funds"
              ? mutualFundsRef.current
              : floatingOpen === "research"
                ? researchRef.current
                : floatingOpen === "security"
                  ? securityRef.current
                  : floatingOpen === "cms"
                    ? cmsRef.current
                    : floatingOpen === "portfolio-management"
                      ? portfolioManagementRef.current
                      : floatingOpen === "invest-online-bse"
                        ? investOnlineBseRef.current
                        : floatingOpen === "utilities"
                          ? utilitiesRef.current
                          : floatingOpen === "brokerage"
                            ? brokerageRef.current
                            : floatingOpen === "risk-profiling"
                              ? riskProfilingRef.current
                              : floatingOpen === "billing"
                                ? billingRef.current
                                : null;
        if (trigger && !trigger.contains(e.target)) setFloatingOpen(null);
      }

      // clicking outside mobile overlay should close it
      if (mobileOpen) {
        const sidebarEl = document.querySelector(".sidebar");
        if (sidebarEl && !sidebarEl.contains(e.target)) {
          setMobileOpen(false);
        }
      }
    }
    function onKey(e) {
      if (e.key === "Escape") {
        setFloatingOpen(null);
        setInlineOpen(null);
        setMobileOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [floatingOpen, mobileOpen]);

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem("sidebar_collapsed", next ? "true" : "false");
  };

  const handleMobileToggle = () => {
    // open the overlay sidebar on mobile
    setMobileOpen((v) => !v);
  };

  const showLogoutSuccess = () => {
    Swal.fire({
      html: `
      <div style="text-align:center;padding:20px 10px;">
        <div style="width:80px;height:80px;margin:0 auto 20px auto;border-radius:50%;background:#4bded63d;display:flex;align-items:center;justify-content:center;">
          <i class="fa fa-check" style="font-size:36px;color:#06d1c6;"></i>
        </div>
        <h2 style="margin:0;font-size:22px;color:#333;">Logged out successfully</h2>
        <p style="margin-top:8px;color:#777;font-size:14px;">
          You have been safely logged out.
        </p>
      </div>
    `,
      showConfirmButton: false,
      timer: 1500,
      backdrop: `rgba(0,0,0,0.4)`,
      customClass: {
        popup: "custom-swal-popup",
      },
    });
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Logout Confirmation",
      html: `
      <div style="text-align:center;">
        <div style="width:70px;height:70px;margin:0 auto 15px auto;border-radius:50%;background:#4bded63d;display:flex;align-items:center;justify-content:center;">
          <i class="fa fa-sign-out-alt" style="font-size:32px;color:#06d1c6;"></i>
        </div>
        <p style="font-size:16px;color:#555;margin:0;">
          Are you sure you want to logout from your account?
        </p>
      </div>
    `,
      showCancelButton: true,
      confirmButtonText: "Yes, Logout",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        popup: "custom-swal-popup",
        confirmButton: "custom-confirm-btn",
        cancelButton: "custom-cancel-btn",
      },
    }).then((res) => {
      if (res.isConfirmed) {
        setLoading(true);
        axios
          .post(`${process.env.REACT_APP_BACKEND_BASE_URL}/user/logout`)
          .then(() => {
            localStorage.removeItem(process.env.REACT_APP_LOGIN_DATA_STORE);
            localStorage.removeItem("authToken");
            showLogoutSuccess();
            setTimeout(() => {
              window.location.href = "/";
            }, 1500);
          })
          .catch(() => {
            setLoading(false);
            Swal.fire("Error", "Network error during logout", "error");
          })
          .finally(() => setLoading(false));
      }
    });
  };

  // if (loading) return <Loader />;

  /* ---------- helpers for dropdown menus ---------- */
  const toggleDropdown = (menuName) => {
    // menuName: "users" | "mutual-funds" | "research" | "security" | "cms" | "portfolio-management" | "invest-online-bse" | "utilities" | "brokerage" | "risk-profiling" | "billing"
    if (isMobile) {
      // On mobile, use inline submenu inside the overlay
      setInlineOpen((v) => (v === menuName ? null : menuName));
      setFloatingOpen(null);
      return;
    }

    // when collapsed (desktop) use floating; when expanded use inline
    if (collapsed) {
      setFloatingOpen((v) => (v === menuName ? null : menuName));
      setInlineOpen(null);
    } else {
      setInlineOpen((v) => (v === menuName ? null : menuName));
      setFloatingOpen(null);
    }
  };

  useEffect(() => {
    document.body.style.overflow = loading ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [loading]);

  return (
    <>
      <style>{`
.custom-swal-popup {
  border-radius: 16px !important;
  padding: 25px !important;
}

.custom-confirm-btn {
  background: linear-gradient(90deg, #06b6a4, #06d1c6) !important;
  color: #fff !important;
  padding: 10px 22px !important;
  border-radius: 8px !important;
  border: none !important;
  margin-right: 10px;
  font-size: 14px;
  transition: 0.3s ease;
}

.custom-confirm-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 15px rgba(6, 209, 198, 0.3);
}

.custom-cancel-btn {
  background: #ef4444 !important;
  color: #fff !important;
  padding: 10px 22px !important;
  border-radius: 8px !important;
  border: none !important;
  font-size: 14px;
  transition: 0.3s ease;
}

.custom-cancel-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 15px rgba(239, 68, 68, 0.3);
}

/* Sidebar base */
:root {
  --sidebar-width: 220px;
  --sidebar-collapsed-width: 56px; /* smaller collapsed width */
}

.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  min-width: var(--sidebar-width);
  width: max-content;
  height: 100vh;
  background: linear-gradient(180deg,#00bfa6 0%, #00a6d6 100%);
  color: #fff;
  display: flex;
  flex-direction: column;
  padding: 14px 10px;
  box-sizing: border-box;
  z-index: 1500;
  transition: min-width 220ms ease, padding 220ms ease;
  overflow-y: auto; /* scroll vertical only */
  overflow-x: hidden; /* no horizontal scroll */
  scroll-behavior: smooth; /* aesthetic scrolling */
  box-shadow: 0 8px 24px rgba(2,6,23,0.08);
}
  .sidebar-nav {
  display:flex;
  flex-direction:column;
  gap:8px;
  margin-top:6px;
  flex: 1;
  overflow-y: scroll;
  padding-right:6px;
  scrollbar-width: none;
}
/* .sidebar-nav { display:flex; flex-direction:column; gap:8px; margin-top:6px; flex: 1; overflow:auto; padding-right:6px; } */

.sidebar-nav::-webkit-scrollbar {
  display: none;
}



.sidebar.collapsed {
  min-width: var(--sidebar-collapsed-width);
  padding: 14px 8px;
}

/* logo */
.logo-wrap { display:flex; align-items:center; gap:12px; margin-bottom: 14px; }
.logo-wrap img { width: 150px; border-radius: 10px; background: #fff; padding: 6px; transition: width 220ms ease, height 220ms ease; }
.sidebar.collapsed .logo-wrap img { width: 40px; height: 40px; }

/* collapse button */
.collapse-btn { margin-left: auto; background: transparent; border: none; color: #fff; cursor: pointer; padding: 6px; font-size: 18px; }


/* menu-item */
.menu-item { display:flex; align-items:center; gap:14px; padding: 10px 12px; border-radius: 10px; color: #eaf8f7; text-decoration: none; font-weight: 600; font-size: 15px; transition: background 140ms ease, transform 120ms ease; position: relative; }
.menu-item .icon { width: 36px; min-width: 36px; height: 36px; display:flex; align-items:center; justify-content:center; font-size: 18px; background: rgba(255,255,255,0.06); border-radius: 8px; margin: 0; }
.menu-item .label { line-height:1; color: #eaf8f7; }

/* hover/active */
.menu-item:hover { background: rgba(255,255,255,0.06); transform: translateX(4px); color: #fff; }
.menu-item.active { background: rgba(255,255,255,0.12); box-shadow: 0 8px 18px rgba(0,0,0,0.08); color: #fff; }

/* collapsed - hide labels and center icons */
.sidebar.collapsed .menu-item { justify-content:center; padding: 8px 6px; }
.sidebar.collapsed .menu-item .label { display:none; }

/* tooltip on hover when collapsed */
.sidebar.collapsed .menu-item::after {
  content: attr(data-label);
  position: absolute;
  left: calc(var(--sidebar-collapsed-width) + 12px);
  top: 50%;
  transform: translateY(-50%) translateX(-6px);
  background: #ffffff;
  color: #0f172a;
  padding: 8px 12px;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(7,12,34,0.12);
  white-space: nowrap;
  font-weight: 700;
  opacity: 0;
  pointer-events: none;
  transition: opacity 160ms ease, transform 160ms ease;
  z-index: 9999;
}
.sidebar.collapsed .menu-item:hover::after { opacity: 1; transform: translateY(-50%) translateX(0); }

/* inline submenu when expanded */
.submenu { margin-left: 6px; margin-top: 6px; display:flex; flex-direction:column; gap:6px; }
.submenu .menu-item { padding-left: 6px; }

/* Floating submenu (when collapsed) - shows icon above and text below */
.floating-submenu {
  position: fixed;
  left: calc(var(--sidebar-collapsed-width) + 8px);
  transform: translateY(-50%);
  min-width: 200px;
      background: linear-gradient(304deg, #00a0fc, #0cc5a2);
  color: #0f172a;
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(7,12,34,0.14);
  padding: 10px;
  z-index: 2600;
  display:flex;
  flex-direction: column;
  gap: 8px;
}

/* vertical layout for floating items (icon above, label below) */
.floating-submenu .menu-item { display:flex; flex-direction: row; align-items:center; padding: 10px 8px; border-radius: 10px; color: #0f172a; }
.floating-submenu .menu-item .icon { width: 44px; height: 44px; font-size: 18px; }
.floating-submenu .menu-item .label { display:block; margin-top:8px; font-weight:700; font-size:15px; color:#fff; }


.service-sub-heading {
  padding-left: 6px;
  font-weight: 700;
  color: #eaf8f7;
  cursor: pointer;
  display:flex;
  align-items:center;
  justify-content:space-between;
  width:100%;
}
.service-sub-items {
  display:flex;
  flex-direction:column;
  gap:8px;
  margin-left: 0px;
  margin-top: 6px;
}

/* main content reflow */
.main-content { margin-left: var(--sidebar-width, 220px); transition: margin-left 220ms ease; padding: 0 20px; box-sizing: border-box; }
body.sidebar-collapsed .main-content { margin-left: var(--sidebar-collapsed-width); }

/* overlay mode when mobileOpen */
.sidebar.overlay {
  transform: translateX(0);
  left: 0;
}

/* responsive: tablet and mobile */
@media (max-width: 991px) {
  /* On tablet/mobile the sidebar will act as an overlay when opened */
  .sidebar {
    height: 100vh !important;
    max-height: 100vh !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    padding-bottom: 50px !important;
  }

  .sidebar.overlay {
    transform: translateX(320px); /* bring into view (we offset by left) */
    left: 0;
  }

  /* collapsed style still respected but overlay uses full icons + labels */
  .sidebar.collapsed {
    min-width: 220px;
    padding: 14px 10px;
  }

  /* when mobile overlay visible, we want main content to be accessible behind a translucent backdrop */
  .mobile-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(10, 15, 20, 0.45);
    z-index: 1400;
  }

  .main-content {
    margin-left: 0 !important;
    padding: 12px;
  }

  /* Make icons slightly smaller on small screens */
  .menu-item { padding: 10px; font-size: 14px; }
}

/* very small phones */
@media (max-width: 575px) {
  .sidebar {
    width: 220px;
    min-width: 220px;
  }
  .logo-wrap img { width: 120px; }
  .menu-item .icon { width: 40px; min-width: 40px; height: 40px; }
}

/* small SweetAlert popup tweaks if you want */
.swal2-border-radius {
  border-radius: 12px !important;
}
  @media (max-width: 991px) {
  .submenu { margin-left: 12px; }
}

      `}</style>

      {/* Mobile top bar when on small screens */}
      {isMobile && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            top: 0,
            height: 70,
            background: "white",
            zIndex: 1401,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "8px 12px",
          }}
        >
          <button
            onClick={handleMobileToggle}
            aria-expanded={mobileOpen}
            aria-controls="mobile-sidebar"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            style={{
              border: "none",
              background: "linear-gradient(90deg,#0bc1ab,#06a89d)",
              color: "#fff",
              borderRadius: 8,
              padding: "8px 10px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              boxShadow: "0 6px 18px rgba(11,183,165,0.08)",
              cursor: "pointer",
            }}
          >
            <i className={`fa ${mobileOpen ? "fa-times" : "fa-bars"}`} />
            {/* <span style={{ fontWeight: 700 }}>Menu</span> */}
          </button>

          <div style={{ marginTop: 12, color: "#0d233e", fontWeight: 700 }}>
            <NavLink
              to="/dashboard"
              style={{ textDecoration: "none", color: "#0d233e" }}
            >
              <img
                src="/images/gpslogo.png"
                alt="GPS logo"
                style={{ width: "30%" }}
              />
            </NavLink>
          </div>
          <div style={{ marginLeft: "auto" }}>
            <BseLoginCard />
          </div>
        </div>
      )}

      {/* Sidebar */}
      <aside
        id="mobile-sidebar"
        className={`sidebar ${collapsed && !isMobile ? "collapsed" : ""} ${mobileOpen && isMobile ? "overlay" : ""
          }`}
        aria-label="Primary"
        style={{ top: isMobile ? 0 : undefined }}
      >
        <div className="logo-wrap" title="GPS">
          <NavLink
            to="/dashboard"
            onClick={() => {
              window.scrollTo({ top: 0 });
              // if mobile open, close after navigate
              if (mobileOpen) setMobileOpen(false);
            }}
            style={{ display: "flex", alignItems: "center", gap: 12 }}
          >
            <img src="/images/gpslogo.png" alt="GPS logo" />
          </NavLink>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* collapse/expand (desktop) */}
            {!isMobile && (
              <button
                className="collapse-btn"
                onClick={toggleCollapse}
                title={collapsed ? "Expand menu" : "Collapse menu"}
                aria-label={collapsed ? "Expand menu" : "Collapse menu"}
              >
                <i
                  className={`fa ${collapsed ? "fa-chevron-right" : "fa-bars"}`}
                />
              </button>
            )}

            {/* on mobile, show close icon inside sidebar */}
            {isMobile && (
              <button
                className="collapse-btn"
                onClick={() => setMobileOpen(false)}
                title="Close menu"
                aria-label="Close menu"
                style={{ color: "#fff" }}
              >
                <i className="fa fa-times" />
              </button>
            )}
          </div>
        </div>

        <nav className="sidebar-nav" role="navigation" aria-label="Main menu">
          {/* Dashboard */}
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              `menu-item ${isActive ? "active" : ""}`
            }
            data-label="Dashboard"
            title="Dashboard"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon">
              <i className="fa-solid fa-house" aria-hidden />
            </span>
            <span className="label">Dashboard</span>
          </NavLink>

          {/* Client - single link to client list */}
          <NavLink
            to="/admin/invest-bse/ucc-list"
            className={({ isActive }) => `menu-item ${isActive ? "active" : ""}`}
            data-label="Client"
            title="Client"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon">
              <i className="fa-solid fa-user-tie" aria-hidden />
            </span>
            <span className="label">Client</span>
          </NavLink>
          <NavLink
            to="/admin/invest-bse/mandate-list"
            className={({ isActive }) => `menu-item ${isActive ? "active" : ""}`}
            data-label="Mandate"
            title="Mandate"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon">
              <i className="fa-solid fa-file-signature" aria-hidden />
            </span>
            <span className="label">Mandate</span>
          </NavLink>

          {/* Folio Lookup - single link */}
          <NavLink
            to="/admin/folio-lookup"
            className={({ isActive }) =>
              `menu-item ${isActive ? "active" : ""}`
            }
            data-label="Folio Lookup"
            title="Folio Lookup"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon">
              <i className="fa-solid fa-search" aria-hidden />
            </span>
            <span className="label">Folio Lookup</span>
          </NavLink>

          {/* Portfolio Management (dropdown) */}
          <div
            ref={portfolioManagementRef}
            className={`menu-item ${inlineOpen === "portfolio-management" && !collapsed ? "active" : ""
              }`}
            data-label="Portfolio Management"
            title="Portfolio Management"
            role="button"
            onClick={() => toggleDropdown("portfolio-management")}
            aria-haspopup="true"
            aria-expanded={
              collapsed ? floatingOpen === "portfolio-management" : inlineOpen === "portfolio-management"
            }
          >
            <span className="icon">
              <i className="fa-solid fa-briefcase" aria-hidden />
            </span>
            <div
              style={{
                display: collapsed && !isMobile ? "none" : "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span className="label">Portfolio Management</span>
              {!isMobile && (
                <i
                  className={`fa fa-chevron-${inlineOpen === "portfolio-management" ? "up" : "down"
                    }`}
                />
              )}
            </div>
          </div>

          {/* Portfolio Management inline submenu */}
          {inlineOpen === "portfolio-management" && (!collapsed || isMobile) && (
            <div className="submenu" role="menu" aria-label="Portfolio Management submenu">
              <NavLink
                to="/admin/portfolio/comprehensive"
                className="menu-item"
                data-label="Comprehensive Portfolio"
                title="Comprehensive Portfolio"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-chart-pie" />
                </span>
                <span className="label">Comprehensive Portfolio</span>
              </NavLink>

              <NavLink
                to="/admin/portfolio/investor-data-mining"
                className="menu-item"
                data-label="Investor Data Mining"
                title="Investor Data Mining"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-database" />
                </span>
                <span className="label">Investor Data Mining</span>
              </NavLink>

              <NavLink
                to="/admin/portfolio/model"
                className="menu-item"
                data-label="Model Portfolio"
                title="Model Portfolio"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-cubes" />
                </span>
                <span className="label">Model Portfolio</span>
              </NavLink>

              <NavLink
                to="/admin/portfolio/recommendations"
                className="menu-item"
                data-label="Recommendations"
                title="Recommendations"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-lightbulb" />
                </span>
                <span className="label">Recommendations</span>
              </NavLink>
            </div>
          )}

          {/* Invest Online BSE (dropdown) */}
          <div
            ref={investOnlineBseRef}
            className={`menu-item ${inlineOpen === "invest-online-bse" && !collapsed ? "active" : ""
              }`}
            data-label="Invest Online BSE"
            title="Invest Online BSE"
            role="button"
            onClick={() => toggleDropdown("invest-online-bse")}
            aria-haspopup="true"
            aria-expanded={
              collapsed ? floatingOpen === "invest-online-bse" : inlineOpen === "invest-online-bse"
            }
          >
            <span className="icon">
              <i className="fa-solid fa-chart-line" aria-hidden />
            </span>
            <div
              style={{
                display: collapsed && !isMobile ? "none" : "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span className="label">Invest Online BSE</span>
              {!isMobile && (
                <i
                  className={`fa fa-chevron-${inlineOpen === "invest-online-bse" ? "up" : "down"
                    }`}
                />
              )}
            </div>
          </div>

          {/* Invest Online BSE inline submenu */}
          {inlineOpen === "invest-online-bse" && (!collapsed || isMobile) && (
            <div className="submenu" role="menu" aria-label="Invest Online BSE submenu">
              <NavLink
                to="/admin/invest-bse/profile-list"
                className="menu-item"
                data-label="Profile List"
                title="Profile List"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-address-book" />
                </span>
                <span className="label">Profile List</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/create-ucc"
                className="menu-item"
                data-label="Create UCC"
                title="Create UCC & Platform User"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-user-plus" />
                </span>
                <span className="label">Create UCC</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/ucc-list"
                className="menu-item"
                data-label="UCC List"
                title="UCC List (BSE)"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-users" />
                </span>
                <span className="label">UCC List</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/mandate-list"
                className="menu-item"
                data-label="Mandate List"
                title="Mandate List"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-file-signature" />
                </span>
                <span className="label">Mandate List</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/create-mandate"
                className="menu-item"
                data-label="Register Mandate"
                title="Register BSE Mandate"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-money-check-dollar" />
                </span>
                <span className="label">Register Mandate</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/my-orders"
                className="menu-item"
                data-label="My Orders"
                title="My Orders"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-shopping-cart" />
                </span>
                <span className="label">My Orders</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/rejected-ucc-mapping"
                className="menu-item"
                data-label="Rejected UCC Mapping"
                title="Rejected UCC Mapping"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-times-circle" />
                </span>
                <span className="label">Rejected UCC Mapping</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/kyc-status-report"
                className="menu-item"
                data-label="KYC Status Report"
                title="KYC Status Report"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-clipboard-check" />
                </span>
                <span className="label">KYC Status Report</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/bulk-order"
                className="menu-item"
                data-label="Bulk Order"
                title="Bulk Order"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-layer-group" />
                </span>
                <span className="label">Bulk Order</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/transfer-holding"
                className="menu-item"
                data-label="Transfer Holding"
                title="Transfer Holding"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-exchange-alt" />
                </span>
                <span className="label">Transfer Holding</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/systematic-orders"
                className="menu-item"
                data-label="Systematic Orders"
                title="Systematic Orders"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-calendar-alt" />
                </span>
                <span className="label">Systematic Orders</span>
              </NavLink>
            </div>
          )}

          {/* Utilities (dropdown) */}
          <div
            ref={utilitiesRef}
            className={`menu-item ${inlineOpen === "utilities" && !collapsed ? "active" : ""
              }`}
            data-label="Utilities"
            title="Utilities"
            role="button"
            onClick={() => toggleDropdown("utilities")}
            aria-haspopup="true"
            aria-expanded={
              collapsed ? floatingOpen === "utilities" : inlineOpen === "utilities"
            }
          >
            <span className="icon">
              <i className="fa-solid fa-wrench" aria-hidden />
            </span>
            <div
              style={{
                display: collapsed && !isMobile ? "none" : "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span className="label">Utilities</span>
              {!isMobile && (
                <i
                  className={`fa fa-chevron-${inlineOpen === "utilities" ? "up" : "down"
                    }`}
                />
              )}
            </div>
          </div>

          {/* Utilities inline submenu */}
          {inlineOpen === "utilities" && (!collapsed || isMobile) && (
            <div className="submenu" role="menu" aria-label="Utilities submenu">
              <NavLink
                to="/admin/utilities/top-schemes"
                className="menu-item"
                data-label="Top Schemes"
                title="Top Schemes"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-trophy" />
                </span>
                <span className="label">Top Schemes</span>
              </NavLink>

              <NavLink
                to="/admin/utilities/task-manager"
                className="menu-item"
                data-label="Task Manager"
                title="Task Manager"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-tasks" />
                </span>
                <span className="label">Task Manager</span>
              </NavLink>

              <NavLink
                to="/admin/utilities/goal-planner"
                className="menu-item"
                data-label="Goal Planner"
                title="Goal Planner"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-bullseye" />
                </span>
                <span className="label">Goal Planner</span>
              </NavLink>

              <NavLink
                to="/admin/utilities/calculators"
                className="menu-item"
                data-label="Calculators"
                title="Calculators"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-calculator" />
                </span>
                <span className="label">Calculators</span>
              </NavLink>

              <NavLink
                to="/admin/utilities/forms"
                className="menu-item"
                data-label="Forms"
                title="Forms"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-file-alt" />
                </span>
                <span className="label">Forms</span>
              </NavLink>

              <NavLink
                to="/admin/utilities/market-data"
                className="menu-item"
                data-label="Market Data"
                title="Market Data"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-chart-bar" />
                </span>
                <span className="label">Market Data</span>
              </NavLink>

              <NavLink
                to="/admin/utilities/scheme-compare"
                className="menu-item"
                data-label="Scheme Compare"
                title="Scheme Compare"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-code-compare" />
                </span>
                <span className="label">Scheme Compare</span>
              </NavLink>

              <NavLink
                to="/admin/utilities/fund-selector"
                className="menu-item"
                data-label="Fund Selector"
                title="Fund Selector"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-filter" />
                </span>
                <span className="label">Fund Selector</span>
              </NavLink>

              <NavLink
                to="/admin/utilities/meeting-notes"
                className="menu-item"
                data-label="Meeting Notes"
                title="Meeting Notes"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-sticky-note" />
                </span>
                <span className="label">Meeting Notes</span>
              </NavLink>
            </div>
          )}

          {/* Brokerage (dropdown) */}
          <div
            ref={brokerageRef}
            className={`menu-item ${inlineOpen === "brokerage" && !collapsed ? "active" : ""}`}
            data-label="Brokerage"
            title="Brokerage"
            role="button"
            onClick={() => toggleDropdown("brokerage")}
            aria-haspopup="true"
            aria-expanded={collapsed ? floatingOpen === "brokerage" : inlineOpen === "brokerage"}
          >
            <span className="icon">
              <i className="fa-solid fa-percent" aria-hidden />
            </span>
            <div style={{ display: collapsed && !isMobile ? "none" : "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <span className="label">Brokerage</span>
              {!isMobile && <i className={`fa fa-chevron-${inlineOpen === "brokerage" ? "up" : "down"}`} />}
            </div>
          </div>
          {inlineOpen === "brokerage" && (!collapsed || isMobile) && (
            <div className="submenu" role="menu" aria-label="Brokerage submenu">
              <NavLink to="/admin/brokerage/income-detail" className="menu-item" data-label="Income Detail" title="Income Detail" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-money-bill-wave" /></span>
                <span className="label">Income Detail</span>
              </NavLink>
              <NavLink to="/admin/brokerage/analysis" className="menu-item" data-label="Analysis" title="Analysis" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-chart-line" /></span>
                <span className="label">Analysis</span>
              </NavLink>
              <NavLink to="/admin/brokerage/payout-process" className="menu-item" data-label="Payout Process" title="Payout Process" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-hand-holding-usd" /></span>
                <span className="label">Payout Process</span>
              </NavLink>
              <NavLink to="/admin/brokerage/verify-payout-post" className="menu-item" data-label="Verify Payout and Post" title="Verify Payout and Post" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-check-double" /></span>
                <span className="label">Verify Payout and Post</span>
              </NavLink>
              <NavLink to="/admin/brokerage/earning-rates" className="menu-item" data-label="Earning Rates" title="Earning Rates" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-coins" /></span>
                <span className="label">Earning Rates</span>
              </NavLink>
              <NavLink to="/admin/brokerage/lost-brokerage" className="menu-item" data-label="Lost Brokerage" title="Lost Brokerage" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-chart-line" /></span>
                <span className="label">Lost Brokerage</span>
              </NavLink>
              <NavLink to="/admin/brokerage/reconciliation" className="menu-item" data-label="Brokerage Reconciliation" title="Brokerage Reconciliation" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-balance-scale" /></span>
                <span className="label">Brokerage Reconciliation</span>
              </NavLink>
            </div>
          )}

          {/* Risk Profiling (dropdown) */}
          <div
            ref={riskProfilingRef}
            className={`menu-item ${inlineOpen === "risk-profiling" && !collapsed ? "active" : ""}`}
            data-label="Risk Profiling"
            title="Risk Profiling"
            role="button"
            onClick={() => toggleDropdown("risk-profiling")}
            aria-haspopup="true"
            aria-expanded={collapsed ? floatingOpen === "risk-profiling" : inlineOpen === "risk-profiling"}
          >
            <span className="icon">
              <i className="fa-solid fa-shield-halved" aria-hidden />
            </span>
            <div style={{ display: collapsed && !isMobile ? "none" : "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <span className="label">Risk Profiling</span>
              {!isMobile && <i className={`fa fa-chevron-${inlineOpen === "risk-profiling" ? "up" : "down"}`} />}
            </div>
          </div>
          {inlineOpen === "risk-profiling" && (!collapsed || isMobile) && (
            <div className="submenu" role="menu" aria-label="Risk Profiling submenu">
              <NavLink to="/admin/risk-profiling/submissions" className="menu-item" data-label="Submissions" title="Submissions" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-file-export" /></span>
                <span className="label">Submissions</span>
              </NavLink>
              <NavLink to="/admin/risk-profiling/setup" className="menu-item" data-label="Setup Risk Profiler" title="Setup Risk Profiler" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-cog" /></span>
                <span className="label">Setup Risk Profiler</span>
              </NavLink>
            </div>
          )}

          {/* Billing (dropdown) */}
          <div
            ref={billingRef}
            className={`menu-item ${inlineOpen === "billing" && !collapsed ? "active" : ""}`}
            data-label="Billing"
            title="Billing"
            role="button"
            onClick={() => toggleDropdown("billing")}
            aria-haspopup="true"
            aria-expanded={collapsed ? floatingOpen === "billing" : inlineOpen === "billing"}
          >
            <span className="icon">
              <i className="fa-solid fa-file-invoice-dollar" aria-hidden />
            </span>
            <div style={{ display: collapsed && !isMobile ? "none" : "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
              <span className="label">Billing</span>
              {!isMobile && <i className={`fa fa-chevron-${inlineOpen === "billing" ? "up" : "down"}`} />}
            </div>
          </div>
          {inlineOpen === "billing" && (!collapsed || isMobile) && (
            <div className="submenu" role="menu" aria-label="Billing submenu">
              <NavLink to="/admin/billing/create-client-bill" className="menu-item" data-label="Create Client Bill" title="Create Client Bill" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-plus-circle" /></span>
                <span className="label">Create Client Bill</span>
              </NavLink>
              <NavLink to="/admin/billing/client-bill-register" className="menu-item" data-label="Client Bill Register" title="Client Bill Register" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-list" /></span>
                <span className="label">Client Bill Register</span>
              </NavLink>
              <NavLink to="/admin/billing/create-amc-bill" className="menu-item" data-label="Create AMC Bill" title="Create AMC Bill" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-file-invoice" /></span>
                <span className="label">Create AMC Bill</span>
              </NavLink>
              <NavLink to="/admin/billing/amc-bill-register" className="menu-item" data-label="AMC Bill Register" title="AMC Bill Register" onClick={() => isMobile && setMobileOpen(false)}>
                <span className="icon"><i className="fa-solid fa-book" /></span>
                <span className="label">AMC Bill Register</span>
              </NavLink>
            </div>
          )}

          {/* Marketing Resources */}
          <NavLink
            to="/admin/marketing-resources"
            className={({ isActive }) => `menu-item ${isActive ? "active" : ""}`}
            data-label="Marketing Resources"
            title="Marketing Resources"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon">
              <i className="fa-solid fa-bullhorn" aria-hidden />
            </span>
            <span className="label">Marketing Resources</span>
          </NavLink>

          {/* Transactions View */}
          <NavLink
            to="/admin/transactions-view"
            className={({ isActive }) => `menu-item ${isActive ? "active" : ""}`}
            data-label="Transactions View"
            title="Transactions View"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon">
              <i className="fa-solid fa-exchange-alt" aria-hidden />
            </span>
            <span className="label">Transactions View</span>
          </NavLink>

          {/* Insurance */}
          <NavLink
            to="/admin/insurance"
            className={({ isActive }) => `menu-item ${isActive ? "active" : ""}`}
            data-label="Insurance"
            title="Insurance"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon">
              <i className="fa-solid fa-shield-halved" aria-hidden />
            </span>
            <span className="label">Insurance</span>
          </NavLink>

          {/* Business Analytics */}
          <NavLink
            to="/admin/business-analytics"
            className={({ isActive }) => `menu-item ${isActive ? "active" : ""}`}
            data-label="Business Analytics"
            title="Business Analytics"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon">
              <i className="fa-solid fa-chart-line" aria-hidden />
            </span>
            <span className="label">Business Analytics</span>
          </NavLink>

          {/* Data Management */}
          <NavLink
            to="/admin/data-management"
            className={({ isActive }) => `menu-item ${isActive ? "active" : ""}`}
            data-label="Data Management"
            title="Data Management"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon">
              <i className="fa-solid fa-database" aria-hidden />
            </span>
            <span className="label">Data Management</span>
          </NavLink>

          {/* Email Scheduler */}
          <NavLink
            to="/admin/email-scheduler"
            className={({ isActive }) => `menu-item ${isActive ? "active" : ""}`}
            data-label="Email Scheduler"
            title="Email Scheduler"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon">
              <i className="fa-solid fa-envelope" aria-hidden />
            </span>
            <span className="label">Email Scheduler</span>
          </NavLink>

          {/* WhatsApp Scheduler */}
          <NavLink
            to="/admin/whatsapp-scheduler"
            className={({ isActive }) => `menu-item ${isActive ? "active" : ""}`}
            data-label="WhatsApp Scheduler"
            title="WhatsApp Scheduler"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon">
              <i className="fa-brands fa-whatsapp" aria-hidden />
            </span>
            <span className="label">WhatsApp Scheduler</span>
          </NavLink>

          {/* Users (dropdown) */}
          <div
            ref={usersRef}
            className={`menu-item ${inlineOpen === "users" && !collapsed ? "active" : ""
              }`}
            data-label="Users"
            title="Users"
            role="button"
            onClick={() => toggleDropdown("users")}
            aria-haspopup="true"
            aria-expanded={
              collapsed ? floatingOpen === "users" : inlineOpen === "users"
            }
          >
            <span className="icon">
              <i className="fa-solid fa-users" aria-hidden />
            </span>
            <div
              style={{
                display: collapsed && !isMobile ? "none" : "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span className="label">Users</span>
              {!isMobile && (
                <i
                  className={`fa fa-chevron-${inlineOpen === "users" ? "up" : "down"
                    }`}
                />
              )}
            </div>
          </div>

          {/* Users inline submenu */}
          {inlineOpen === "users" && (!collapsed || isMobile) && (
            <div className="submenu" role="menu" aria-label="Users submenu">
              <NavLink
                to="/admin/users/directory"
                className="menu-item"
                data-label="User Directory"
                title="User Directory"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-list" />
                </span>
                <span className="label">User Directory</span>
              </NavLink>

              <NavLink
                to="/user"
                className="menu-item"
                data-label="Users List"
                title="Users List"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-users" />
                </span>
                <span className="label">Users List</span>
              </NavLink>

              <NavLink
                to="/admin/users/kyc-review"
                className="menu-item"
                data-label="KYC Review Queue"
                title="KYC Review Queue"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-clipboard-check" />
                </span>
                <span className="label">KYC Review Queue</span>
              </NavLink>

              <NavLink
                to="/admin/users/bank-verification"
                className="menu-item"
                data-label="Bank Verification Queue"
                title="Bank Verification Queue"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-university" />
                </span>
                <span className="label">Bank Verification Queue</span>
              </NavLink>
            </div>
          )}

          {/* Mutual Funds (dropdown) */}
          <div
            ref={mutualFundsRef}
            className={`menu-item ${inlineOpen === "mutual-funds" && !collapsed ? "active" : ""
              }`}
            data-label="Mutual Funds"
            title="Mutual Funds"
            role="button"
            onClick={() => toggleDropdown("mutual-funds")}
            aria-haspopup="true"
            aria-expanded={
              collapsed
                ? floatingOpen === "mutual-funds"
                : inlineOpen === "mutual-funds"
            }
          >
            <span className="icon">
              <i className="fa-solid fa-coins" aria-hidden />
            </span>
            <div
              style={{
                display: collapsed && !isMobile ? "none" : "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span className="label">Mutual Funds</span>
              {!isMobile && (
                <i
                  className={`fa fa-chevron-${inlineOpen === "mutual-funds" ? "up" : "down"
                    }`}
                />
              )}
            </div>
          </div>

          {/* Mutual Funds inline submenu */}
          {inlineOpen === "mutual-funds" && (!collapsed || isMobile) && (
            <div
              className="submenu"
              role="menu"
              aria-label="Mutual Funds submenu"
            >
              <NavLink
                to="/admin/mutual-funds/master-data"
                className="menu-item"
                data-label="Master Data"
                title="Master Data (AMCs & Schemes)"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-database" />
                </span>
                <span className="label">Master Data</span>
              </NavLink>

              <NavLink
                to="/mutualfund"
                className="menu-item"
                data-label="Mutual Funds List"
                title="Mutual Funds List"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-list" />
                </span>
                <span className="label">Mutual Funds List</span>
              </NavLink>

              {/* <NavLink
                to="/create-mutualfund"
                className="menu-item"
                data-label="Create Mutual Fund"
                title="Create Mutual Fund"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-plus-circle" />
                </span>
                <span className="label">Create Mutual Fund</span>
              </NavLink> */}

              <NavLink
                to="/admin/mutual-funds/orders-monitor"
                className="menu-item"
                data-label="Orders Monitor"
                title="Orders Monitor"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-chart-line" />
                </span>
                <span className="label">Orders Monitor</span>
              </NavLink>

              <NavLink
                to="/admin/mutual-funds/sip-monitor"
                className="menu-item"
                data-label="SIP Monitor"
                title="SIP Monitor"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-calendar-check" />
                </span>
                <span className="label">SIP Monitor</span>
              </NavLink>

              <NavLink
                to="/admin/mutual-funds/mandates"
                className="menu-item"
                data-label="Mandates"
                title="Mandates"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-file-signature" />
                </span>
                <span className="label">Mandates</span>
              </NavLink>

              <NavLink
                to="/admin/mutual-funds/reconciliation"
                className="menu-item"
                data-label="Reconciliation"
                title="Reconciliation"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-balance-scale" />
                </span>
                <span className="label">Reconciliation</span>
              </NavLink>

              <NavLink
                to="/investment-enquiry"
                className="menu-item"
                data-label="Investment Enquiry"
                title="Investment Enquiry"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-question-circle" />
                </span>
                <span className="label">Investment Enquiry</span>
              </NavLink>

              <NavLink
                to="/admin/agents/directory"
                className="menu-item"
                data-label="Agent Directory"
                title="Agent Directory"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-address-book" />
                </span>
                <span className="label">Agent Directory</span>
              </NavLink>

              <NavLink
                to="/admin/agents/commission-rules"
                className="menu-item"
                data-label="Commission Rules"
                title="Commission Rules"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-percent" />
                </span>
                <span className="label">Commission Rules</span>
              </NavLink>
            </div>
          )}

          {/* Research (dropdown) */}
          {/* <div
            ref={researchRef}
            className={`menu-item ${inlineOpen === "research" && !collapsed ? "active" : ""
              }`}
            data-label="Research"
            title="Research"
            role="button"
            onClick={() => toggleDropdown("research")}
            aria-haspopup="true"
            aria-expanded={
              collapsed
                ? floatingOpen === "research"
                : inlineOpen === "research"
            }
          >
            <span className="icon">
              <i className="fa-solid fa-microscope" aria-hidden />
            </span>
            <div
              style={{
                display: collapsed && !isMobile ? "none" : "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span className="label">Research</span>
              {!isMobile && (
                <i
                  className={`fa fa-chevron-${inlineOpen === "research" ? "up" : "down"
                    }`}
                />
              )}
            </div>
          </div>

          {inlineOpen === "research" && (!collapsed || isMobile) && (
            <div className="submenu" role="menu" aria-label="Research submenu">
              <NavLink
                to="/admin/research/content-manager"
                className="menu-item"
                data-label="Content Manager"
                title="Content Manager"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-file-edit" />
                </span>
                <span className="label">Content Manager</span>
              </NavLink>

              <NavLink
                to="/admin/research/approval-workflow"
                className="menu-item"
                data-label="Approval Workflow"
                title="Approval Workflow"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-check-double" />
                </span>
                <span className="label">Approval Workflow</span>
              </NavLink>

              <NavLink
                to="/admin/research/plans-pricing"
                className="menu-item"
                data-label="Plans & Pricing"
                title="Plans & Pricing"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-tags" />
                </span>
                <span className="label">Plans & Pricing</span>
              </NavLink>

              <NavLink
                to="/admin/research/subscriptions"
                className="menu-item"
                data-label="Subscriptions"
                title="Subscriptions"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-credit-card" />
                </span>
                <span className="label">Subscriptions</span>
              </NavLink>
            </div>
          )} */}

          {/* Billing */}
          <NavLink
            to="/admin/billing/invoices-payments"
            className={({ isActive }) =>
              `menu-item ${isActive ? "active" : ""}`
            }
            data-label="Billing"
            title="Invoices & Payments"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon">
              <i className="fa-solid fa-receipt" />
            </span>
            <span className="label">Billing</span>
          </NavLink>

          {/* Loan Application */}
          <NavLink
            to="/loan-application"
            className={({ isActive }) =>
              `menu-item ${isActive ? "active" : ""}`
            }
            data-label="Loan Application"
            title="Loan Application"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon">
              <i className="fa-solid fa-file-invoice-dollar" />
            </span>
            <span className="label">Loan Application</span>
          </NavLink>

          {/* Support */}
          <NavLink
            to="/admin/support/ticket-console"
            className={({ isActive }) =>
              `menu-item ${isActive ? "active" : ""}`
            }
            data-label="Support"
            title="Ticket Console"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon">
              <i className="fa-solid fa-headset" />
            </span>
            <span className="label">Support</span>
          </NavLink>

          {/* Security (dropdown) */}
          <div
            ref={securityRef}
            className={`menu-item ${inlineOpen === "security" && !collapsed ? "active" : ""
              }`}
            data-label="Security"
            title="Security"
            role="button"
            onClick={() => toggleDropdown("security")}
            aria-haspopup="true"
            aria-expanded={
              collapsed
                ? floatingOpen === "security"
                : inlineOpen === "security"
            }
          >
            <span className="icon">
              <i className="fa-solid fa-shield-halved" aria-hidden />
            </span>
            <div
              style={{
                display: collapsed && !isMobile ? "none" : "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span className="label">Security</span>
              {!isMobile && (
                <i
                  className={`fa fa-chevron-${inlineOpen === "security" ? "up" : "down"
                    }`}
                />
              )}
            </div>
          </div>

          {/* Security inline submenu */}
          {inlineOpen === "security" && (!collapsed || isMobile) && (
            <div className="submenu" role="menu" aria-label="Security submenu">
              <NavLink
                to="/admin/security/roles-permissions"
                className="menu-item"
                data-label="Roles & Permissions"
                title="Roles & Permissions"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-user-lock" />
                </span>
                <span className="label">Roles & Permissions</span>
              </NavLink>

              <NavLink
                to="/admin/security/audit-logs"
                className="menu-item"
                data-label="Audit Logs"
                title="Audit Logs"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-clipboard-list" />
                </span>
                <span className="label">Audit Logs</span>
              </NavLink>

              <NavLink
                to="/admin/security/change-password"
                className="menu-item"
                data-label="Change BSE Password"
                title="Change BSE Password"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-key" />
                </span>
                <span className="label">Change BSE Password</span>
              </NavLink>
            </div>
          )}

          {/* CMS (dropdown) */}
          <div
            ref={cmsRef}
            className={`menu-item ${inlineOpen === "cms" && !collapsed ? "active" : ""
              }`}
            data-label="CMS"
            title="CMS"
            role="button"
            onClick={() => toggleDropdown("cms")}
            aria-haspopup="true"
            aria-expanded={
              collapsed ? floatingOpen === "cms" : inlineOpen === "cms"
            }
          >
            <span className="icon">
              <i className="fa-solid fa-globe" aria-hidden />
            </span>
            <div
              style={{
                display: collapsed && !isMobile ? "none" : "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span className="label">CMS</span>
              {!isMobile && (
                <i
                  className={`fa fa-chevron-${inlineOpen === "cms" ? "up" : "down"
                    }`}
                />
              )}
            </div>
          </div>

          {/* CMS inline submenu */}
          {inlineOpen === "cms" && (!collapsed || isMobile) && (
            <div className="submenu" role="menu" aria-label="CMS submenu">
              {/* <NavLink
                to="/admin/cms/website-pages"
                className="menu-item"
                data-label="Website Pages"
                title="Website Pages"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-file-alt" />
                </span>
                <span className="label">Website Pages</span>
              </NavLink> */}

              <NavLink
                to="/terms&condition-update"
                className="menu-item"
                data-label="Terms & Conditions"
                title="Terms & Conditions"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-file-contract" />
                </span>
                <span className="label">Terms & Conditions</span>
              </NavLink>

              <NavLink
                to="/privacy-policy-update"
                className="menu-item"
                data-label="Privacy Policy"
                title="Privacy Policy"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-shield-alt" />
                </span>
                <span className="label">Privacy Policy</span>
              </NavLink>

              <NavLink
                to="/disclaimer-update"
                className="menu-item"
                data-label="Disclaimer"
                title="Disclaimer"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-exclamation-triangle" />
                </span>
                <span className="label">Disclaimer</span>
              </NavLink>

              <NavLink
                to="/bloglist"
                className="menu-item"
                data-label="Blog List"
                title="Blog List"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-blog" />
                </span>
                <span className="label">Blog List</span>
              </NavLink>

              {/* <NavLink
                to="/create-blog"
                className="menu-item"
                data-label="Create Blog"
                title="Create Blog"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-plus-circle" />
                </span>
                <span className="label">Create Blog</span>
              </NavLink> */}
            </div>
          )}

          {/* Integrations */}
          <NavLink
            to="/admin/integrations/webhooks-jobs"
            className={({ isActive }) =>
              `menu-item ${isActive ? "active" : ""}`
            }
            data-label="Integrations"
            title="Webhooks & Jobs"
            onClick={() => isMobile && setMobileOpen(false)}
          >
            <span className="icon">
              <i className="fa-solid fa-plug" />
            </span>
            <span className="label">Integrations</span>
          </NavLink>

          {/* BSE Login - visible on all admin pages */}
          {/* <div
            className="px-3 py-2"
            style={{
              borderTop: "1px solid rgba(255,255,255,0.1)",
              marginTop: 8,
              display: collapsed && !isMobile ? "none" : "block",
            }}
          >
            <BseLoginCard />
          </div> */}

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="menu-item"
            data-label="Logout"
            title="Logout"
            style={{
              background: "transparent",
              border: "none",
              textAlign: "left",
              width: "100%",
              marginTop: 8,
            }}
          >
            <span className="icon">
              <i className="fa-solid fa-arrow-right-from-bracket" />
            </span>
            <span className="label">Logout</span>
          </button>
        </nav>
      </aside>

      {/* Backdrop for mobile overlay */}
      {isMobile && mobileOpen && (
        <div
          className="mobile-backdrop"
          role="button"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Floating submenu when collapsed - shows both icon & text (vertical pill layout) */}
      {floatingOpen && !isMobile && (
        <div
          ref={floatingRef}
          className="floating-submenu"
          role="menu"
          aria-label={`${floatingOpen} submenu`}
          style={{ top: "55%" }}
        >
          {floatingOpen === "users" && (
            <>
              <NavLink
                to="/admin/users/directory"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-list" />
                </span>
                <span className="label">User Directory</span>
              </NavLink>

              <NavLink
                to="/user"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-users" />
                </span>
                <span className="label">Users List</span>
              </NavLink>

              <NavLink
                to="/admin/users/kyc-review"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-clipboard-check" />
                </span>
                <span className="label">KYC Review Queue</span>
              </NavLink>

              <NavLink
                to="/admin/users/bank-verification"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-university" />
                </span>
                <span className="label">Bank Verification Queue</span>
              </NavLink>
            </>
          )}

          {floatingOpen === "mutual-funds" && (
            <>
              <NavLink
                to="/admin/mutual-funds/master-data"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-database" />
                </span>
                <span className="label">Master Data</span>
              </NavLink>

              <NavLink
                to="/mutualfund"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-list" />
                </span>
                <span className="label">Mutual Funds List</span>
              </NavLink>

              <NavLink
                to="/create-mutualfund"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-plus-circle" />
                </span>
                <span className="label">Create Mutual Fund</span>
              </NavLink>

              <NavLink
                to="/admin/mutual-funds/orders-monitor"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-chart-line" />
                </span>
                <span className="label">Orders Monitor</span>
              </NavLink>

              <NavLink
                to="/admin/mutual-funds/sip-monitor"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-calendar-check" />
                </span>
                <span className="label">SIP Monitor</span>
              </NavLink>

              <NavLink
                to="/admin/mutual-funds/mandates"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-file-signature" />
                </span>
                <span className="label">Mandates</span>
              </NavLink>

              <NavLink
                to="/admin/mutual-funds/reconciliation"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-balance-scale" />
                </span>
                <span className="label">Reconciliation</span>
              </NavLink>

              <NavLink
                to="/investment-enquiry"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-question-circle" />
                </span>
                <span className="label">Investment Enquiry</span>
              </NavLink>

              <NavLink
                to="/admin/agents/directory"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-address-book" />
                </span>
                <span className="label">Agent Directory</span>
              </NavLink>

              <NavLink
                to="/admin/agents/commission-rules"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-percent" />
                </span>
                <span className="label">Commission Rules</span>
              </NavLink>
            </>
          )}

          {floatingOpen === "research" && (
            <>
              <NavLink
                to="/admin/research/content-manager"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-file-edit" />
                </span>
                <span className="label">Content Manager</span>
              </NavLink>

              <NavLink
                to="/admin/research/approval-workflow"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-check-double" />
                </span>
                <span className="label">Approval Workflow</span>
              </NavLink>

              <NavLink
                to="/admin/research/plans-pricing"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-tags" />
                </span>
                <span className="label">Plans & Pricing</span>
              </NavLink>

              <NavLink
                to="/admin/research/subscriptions"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-credit-card" />
                </span>
                <span className="label">Subscriptions</span>
              </NavLink>
            </>
          )}

          {floatingOpen === "security" && (
            <>
              <NavLink
                to="/admin/security/roles-permissions"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-user-lock" />
                </span>
                <span className="label">Roles & Permissions</span>
              </NavLink>

              <NavLink
                to="/admin/security/audit-logs"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-clipboard-list" />
                </span>
                <span className="label">Audit Logs</span>
              </NavLink>
            </>
          )}

          {floatingOpen === "cms" && (
            <>
              <NavLink
                to="/admin/cms/website-pages"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-file-alt" />
                </span>
                <span className="label">Website Pages</span>
              </NavLink>

              <NavLink
                to="/terms&condition-update"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-file-contract" />
                </span>
                <span className="label">Terms & Conditions</span>
              </NavLink>

              <NavLink
                to="/privacy-policy-update"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-shield-alt" />
                </span>
                <span className="label">Privacy Policy</span>
              </NavLink>

              <NavLink
                to="/disclaimer-update"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-exclamation-triangle" />
                </span>
                <span className="label">Disclaimer</span>
              </NavLink>

              <NavLink
                to="/bloglist"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-blog" />
                </span>
                <span className="label">Blog List</span>
              </NavLink>

              <NavLink
                to="/create-blog"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-plus-circle" />
                </span>
                <span className="label">Create Blog</span>
              </NavLink>
            </>
          )}

          {floatingOpen === "portfolio-management" && (
            <>
              <NavLink
                to="/admin/portfolio/comprehensive"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-chart-pie" />
                </span>
                <span className="label">Comprehensive Portfolio</span>
              </NavLink>

              <NavLink
                to="/admin/portfolio/investor-data-mining"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-database" />
                </span>
                <span className="label">Investor Data Mining</span>
              </NavLink>

              <NavLink
                to="/admin/portfolio/model"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-cubes" />
                </span>
                <span className="label">Model Portfolio</span>
              </NavLink>

              <NavLink
                to="/admin/portfolio/recommendations"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-lightbulb" />
                </span>
                <span className="label">Recommendations</span>
              </NavLink>
            </>
          )}

          {floatingOpen === "invest-online-bse" && (
            <>
              <NavLink
                to="/admin/invest-bse/profile-list"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-address-book" />
                </span>
                <span className="label">Profile List</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/create-ucc"
                className="menu-item"
                data-label="Create UCC"
                title="Create UCC & Platform User"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <span className="icon">
                  <i className="fa-solid fa-user-plus" />
                </span>
                <span className="label">Create UCC</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/ucc-list"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-users" />
                </span>
                <span className="label">UCC List</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/mandate-list"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-file-signature" />
                </span>
                <span className="label">Mandate List</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/create-mandate"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-money-check-dollar" />
                </span>
                <span className="label">Register Mandate</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/my-orders"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-shopping-cart" />
                </span>
                <span className="label">My Orders</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/rejected-ucc-mapping"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-times-circle" />
                </span>
                <span className="label">Rejected UCC Mapping</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/kyc-status-report"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-clipboard-check" />
                </span>
                <span className="label">KYC Status Report</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/bulk-order"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-layer-group" />
                </span>
                <span className="label">Bulk Order</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/transfer-holding"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-exchange-alt" />
                </span>
                <span className="label">Transfer Holding</span>
              </NavLink>

              <NavLink
                to="/admin/invest-bse/systematic-orders"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-calendar-alt" />
                </span>
                <span className="label">Systematic Orders</span>
              </NavLink>
            </>
          )}

          {floatingOpen === "utilities" && (
            <>
              <NavLink
                to="/admin/utilities/top-schemes"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-trophy" />
                </span>
                <span className="label">Top Schemes</span>
              </NavLink>

              <NavLink
                to="/admin/utilities/task-manager"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-tasks" />
                </span>
                <span className="label">Task Manager</span>
              </NavLink>

              <NavLink
                to="/admin/utilities/goal-planner"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-bullseye" />
                </span>
                <span className="label">Goal Planner</span>
              </NavLink>

              <NavLink
                to="/admin/utilities/calculators"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-calculator" />
                </span>
                <span className="label">Calculators</span>
              </NavLink>

              <NavLink
                to="/admin/utilities/forms"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-file-alt" />
                </span>
                <span className="label">Forms</span>
              </NavLink>

              <NavLink
                to="/admin/utilities/market-data"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-chart-bar" />
                </span>
                <span className="label">Market Data</span>
              </NavLink>

              <NavLink
                to="/admin/utilities/scheme-compare"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-code-compare" />
                </span>
                <span className="label">Scheme Compare</span>
              </NavLink>

              <NavLink
                to="/admin/utilities/fund-selector"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-filter" />
                </span>
                <span className="label">Fund Selector</span>
              </NavLink>

              <NavLink
                to="/admin/utilities/meeting-notes"
                className="menu-item"
                onClick={() => setFloatingOpen(null)}
              >
                <span className="icon">
                  <i className="fa-solid fa-sticky-note" />
                </span>
                <span className="label">Meeting Notes</span>
              </NavLink>
            </>
          )}

          {floatingOpen === "brokerage" && (
            <>
              <NavLink to="/admin/brokerage/income-detail" className="menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-money-bill-wave" /></span>
                <span className="label">Income Detail</span>
              </NavLink>
              <NavLink to="/admin/brokerage/analysis" className="menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-chart-line" /></span>
                <span className="label">Analysis</span>
              </NavLink>
              <NavLink to="/admin/brokerage/payout-process" className="menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-hand-holding-usd" /></span>
                <span className="label">Payout Process</span>
              </NavLink>
              <NavLink to="/admin/brokerage/verify-payout-post" className="menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-check-double" /></span>
                <span className="label">Verify Payout and Post</span>
              </NavLink>
              <NavLink to="/admin/brokerage/earning-rates" className="menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-coins" /></span>
                <span className="label">Earning Rates</span>
              </NavLink>
              <NavLink to="/admin/brokerage/lost-brokerage" className="menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-chart-line" /></span>
                <span className="label">Lost Brokerage</span>
              </NavLink>
              <NavLink to="/admin/brokerage/reconciliation" className="menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-balance-scale" /></span>
                <span className="label">Brokerage Reconciliation</span>
              </NavLink>
            </>
          )}

          {floatingOpen === "risk-profiling" && (
            <>
              <NavLink to="/admin/risk-profiling/submissions" className="menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-file-export" /></span>
                <span className="label">Submissions</span>
              </NavLink>
              <NavLink to="/admin/risk-profiling/setup" className="menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-cog" /></span>
                <span className="label">Setup Risk Profiler</span>
              </NavLink>
            </>
          )}

          {floatingOpen === "billing" && (
            <>
              <NavLink to="/admin/billing/create-client-bill" className="menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-plus-circle" /></span>
                <span className="label">Create Client Bill</span>
              </NavLink>
              <NavLink to="/admin/billing/client-bill-register" className="menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-list" /></span>
                <span className="label">Client Bill Register</span>
              </NavLink>
              <NavLink to="/admin/billing/create-amc-bill" className="menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-file-invoice" /></span>
                <span className="label">Create AMC Bill</span>
              </NavLink>
              <NavLink to="/admin/billing/amc-bill-register" className="menu-item" onClick={() => setFloatingOpen(null)}>
                <span className="icon"><i className="fa-solid fa-book" /></span>
                <span className="label">AMC Bill Register</span>
              </NavLink>
            </>
          )}

        </div>
      )}

      {/* main content wrapper: pages should be in .main-content so they reflow when collapsed */}
      {/* <div
        className="main-content"
        aria-live="polite"
        style={{ minHeight: "100vh", paddingTop: isMobile ? 68 : undefined }}
      /> */}

      {loading && <Loader />}
    </>
  );
}
